import React, { useState, useMemo, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { PieChart, Pie, Cell, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Area, AreaChart, Legend } from 'recharts';
import { FaArrowLeft } from 'react-icons/fa';

function UtilityScenarioStudy() {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const [selectedMode, setSelectedMode] = useState('passenger');
    const [selectedYear, setSelectedYear] = useState('2026');
    const [selectedCase, setSelectedCase] = useState('daily');
    
    // Get element type and id from URL parameters
    const elementType = searchParams.get('type');
    const elementId = searchParams.get('id');
    
    // Generate page title based on URL parameters
    const pageTitle = useMemo(() => {
        if (elementType && elementId) {
            const formattedType = elementType.charAt(0).toUpperCase() + elementType.slice(1);
            return `Load Profile Summary for ${formattedType}: ${elementId}`;
        }
        return 'Load Profile Summary for Long Island, NY, 11101';
    }, [elementType, elementId]);

    
    // Mock data for pie chart - Load Distribution
    const loadDistributionData = [
        { name: 'Passenger EVs', value: 70, color: '#9333ea' },
        { name: 'Commercial Fleets', value: 18, color: '#a855f7' },
        { name: 'Transit Buses', value: 10, color: '#c084fc' },
        { name: 'Off-Road Vehicles', value: 2, color: '#d8b4fe' },
    ];

    // Mock data for typical cases line chart - with separate data for each sector
    const generateDailyData = (baseLoad, sectorMultiplier) => {
        return Array.from({ length: 24 }, (_, i) => ({
            hour: i,
            load: (baseLoad + Math.random() * 40 + Math.sin(i / 3) * 20) * sectorMultiplier,
        }));
    };

    const generateWeeklyData = (baseLoad, sectorMultiplier) => {
        return Array.from({ length: 7 }, (_, i) => ({
            day: ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'][i],
            load: (60 + Math.random() * 30) * sectorMultiplier,
        }));
    };

    const generateWeekendData = (baseLoad, sectorMultiplier) => {
        return Array.from({ length: 48 }, (_, i) => ({
            hour: i / 2,
            load: (40 + Math.random() * 25 + Math.sin(i / 4) * 15) * sectorMultiplier,
        }));
    };

    // Generate data for all sectors - memoized for performance
    const chartData = useMemo(() => {
        const isAllSectors = selectedMode === 'all';
        
        if (isAllSectors) {
            // Generate combined data with all three sectors
            let baseData;
            if (selectedCase === 'daily') {
                baseData = Array.from({ length: 24 }, (_, i) => ({ hour: i }));
            } else if (selectedCase === 'weekly') {
                baseData = Array.from({ length: 7 }, (_, i) => ({ 
                    day: ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'][i] 
                }));
            } else {
                baseData = Array.from({ length: 48 }, (_, i) => ({ hour: i / 2 }));
            }

            // Add load data for each sector with different patterns
            return baseData.map((item, i) => {
                let passengerLoad, commercialLoad, transitLoad;
                
                if (selectedCase === 'daily') {
                    passengerLoad = (50 + Math.random() * 40 + Math.sin(i / 3) * 20) * 0.6;
                    commercialLoad = (30 + Math.random() * 25 + Math.sin((i + 6) / 4) * 15) * 0.3;
                    transitLoad = (20 + Math.random() * 15 + Math.sin((i + 12) / 5) * 10) * 0.1;
                } else if (selectedCase === 'weekly') {
                    passengerLoad = (60 + Math.random() * 30) * 0.6;
                    commercialLoad = (40 + Math.random() * 20) * 0.3;
                    transitLoad = (25 + Math.random() * 15) * 0.1;
                } else {
                    passengerLoad = (40 + Math.random() * 25 + Math.sin(i / 4) * 15) * 0.6;
                    commercialLoad = (25 + Math.random() * 15 + Math.sin((i + 8) / 3) * 10) * 0.3;
                    transitLoad = (15 + Math.random() * 10 + Math.sin((i + 16) / 4) * 8) * 0.1;
                }

                return {
                    ...item,
                    passengerLoad: Math.max(0, passengerLoad),
                    commercialLoad: Math.max(0, commercialLoad),
                    transitLoad: Math.max(0, transitLoad),
                };
            });
        } else {
            // Single sector mode
            let sectorMultiplier = 1;
            if (selectedMode === 'commercial') sectorMultiplier = 0.5;
            if (selectedMode === 'transit') sectorMultiplier = 0.2;

            switch (selectedCase) {
                case 'daily':
                    return generateDailyData(50, sectorMultiplier);
                case 'weekly':
                    return generateWeeklyData(60, sectorMultiplier);
                case 'weekend':
                    return generateWeekendData(40, sectorMultiplier);
                default:
                    return generateDailyData(50, sectorMultiplier);
            }
        }
    }, [selectedMode, selectedCase]);

    const getXAxisKey = () => {
        return selectedCase === 'weekly' ? 'day' : 'hour';
    };

    return (
        <div className="min-h-screen bg-gray-50 font-ui">
            <div className="p-8">
                    {/* Header */}
                    <div className="mb-8">
                        <button
                            onClick={() => navigate(-1)}
                            className="flex items-center gap-2 px-4 py-2 mb-4 bg-white border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 hover:border-gray-400 transition-all duration-200 shadow-sm hover:shadow-md font-ui font-medium"
                            title="Go Back"
                        >
                            <FaArrowLeft className="text-lg" />
                            <span>Go Back</span>
                        </button>
                        <h1 className="text-3xl font-bold text-gray-800">
                            {pageTitle}
                        </h1>
                    </div>

                    {/* Summary and Pie Chart Row */}
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
                        {/* Summary Box */}
                        <div className="bg-gray-200 rounded-lg p-6">
                            <h2 className="text-xl font-bold text-gray-800 mb-4">Summary</h2>
                            <ul className="space-y-2 text-gray-800">
                                <li className="font-semibold">• Key assumptions</li>
                                <ul className="ml-6 space-y-1">
                                    <li>• EV adoption rate: 45% by 2026</li>
                                    <li>• Average daily commute: 32 miles</li>
                                    <li>• Peak charging hours: 6PM - 10PM</li>
                                </ul>
                                <li className="font-semibold mt-3">• Summary</li>
                                <ul className="ml-6 space-y-1">
                                    <li>• Total estimated EV load: 125 MW</li>
                                    <li>• Peak demand increase: 18%</li>
                                    <li>• Off-peak capacity: 87 MW</li>
                                </ul>
                                <li className="font-semibold mt-3">• Key insights</li>
                                <ul className="ml-6 space-y-1">
                                    <li>• Residential charging dominates evening peak</li>
                                    <li>• Commercial corridors show midday demand</li>
                                    <li>• Weekend patterns differ significantly</li>
                                </ul>
                            </ul>
                        </div>

                        {/* Pie Chart */}
                        <div className="bg-white rounded-lg shadow-sm p-6">
                            <h2 className="text-xl font-bold text-gray-800 mb-4 text-center">
                                EV Load Distribution
                            </h2>
                            <ResponsiveContainer width="100%" height={300}>
                                <PieChart>
                                    <Pie
                                        data={loadDistributionData}
                                        cx="50%"
                                        cy="50%"
                                        labelLine={false}
                                        label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                                        outerRadius={100}
                                        fill="#8884d8"
                                        dataKey="value"
                                    >
                                        {loadDistributionData.map((entry, index) => (
                                            <Cell key={`cell-${index}`} fill={entry.color} />
                                        ))}
                                    </Pie>
                                    <Tooltip />
                                </PieChart>
                            </ResponsiveContainer>
                        </div>
                    </div>

                    {/* Selectors */}
                    <div className="flex gap-4 mb-6">
                        <div className="flex items-center gap-2">
                            <label className="text-sm font-medium text-gray-700">Select a sector:</label>
                            <select
                                value={selectedMode}
                                onChange={(e) => setSelectedMode(e.target.value)}
                                className="px-4 py-2 bg-gray-200 rounded border-none text-gray-800 focus:outline-none focus:ring-2 focus:ring-blue-500"
                            >
                                <option value="all">All Sectors</option>
                                <option value="passenger">Passenger</option>
                                <option value="commercial">Commercial</option>
                                <option value="transit">Transit</option>
                            </select>
                        </div>

                        <div className="flex items-center gap-2">
                            <label className="text-sm font-medium text-gray-700">Select a year:</label>
                            <select
                                value={selectedYear}
                                onChange={(e) => setSelectedYear(e.target.value)}
                                className="px-4 py-2 bg-gray-200 rounded border-none text-gray-800 focus:outline-none focus:ring-2 focus:ring-blue-500"
                            >
                                <option value="2024">2026</option>
                                <option value="2025">2027</option>
                                <option value="2026">2028</option>
                                <option value="2027">2029</option>
                                <option value="2028">2030</option>
                                <option value="2030">2031</option>
                            </select>
                        </div>
                    </div>

                    {/* Typical Cases Section */}
                    <div className="bg-white rounded-lg shadow-sm p-6 mb-8">
                        <div className="flex items-center gap-4 mb-6">
                            <h3 className="text-lg font-semibold text-gray-800">Typical Cases</h3>
                            <div className="flex gap-2">
                                <button
                                    onClick={() => setSelectedCase('daily')}
                                    className={`px-6 py-2 rounded-lg font-medium transition-colors ${
                                        selectedCase === 'daily'
                                            ? 'bg-gray-400 text-white'
                                            : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                                    }`}
                                >
                                    Daily
                                </button>
                                <button
                                    onClick={() => setSelectedCase('weekly')}
                                    className={`px-6 py-2 rounded-lg font-medium transition-colors ${
                                        selectedCase === 'weekly'
                                            ? 'bg-gray-400 text-white'
                                            : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                                    }`}
                                >
                                    Weekly
                                </button>
                                <button
                                    onClick={() => setSelectedCase('weekend')}
                                    className={`px-6 py-2 rounded-lg font-medium transition-colors ${
                                        selectedCase === 'weekend'
                                            ? 'bg-gray-400 text-white'
                                            : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                                    }`}
                                >
                                    Weekend
                                </button>
                            </div>
                        </div>

                        {/* Line Chart */}
                        <ResponsiveContainer width="100%" height={300}>
                            <AreaChart data={chartData}>
                                <defs>
                                    <linearGradient id="colorPassenger" x1="0" y1="0" x2="0" y2="1">
                                        <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.4}/>
                                        <stop offset="95%" stopColor="#3b82f6" stopOpacity={0.1}/>
                                    </linearGradient>
                                    <linearGradient id="colorCommercial" x1="0" y1="0" x2="0" y2="1">
                                        <stop offset="5%" stopColor="#10b981" stopOpacity={0.4}/>
                                        <stop offset="95%" stopColor="#10b981" stopOpacity={0.1}/>
                                    </linearGradient>
                                    <linearGradient id="colorTransit" x1="0" y1="0" x2="0" y2="1">
                                        <stop offset="5%" stopColor="#f59e0b" stopOpacity={0.4}/>
                                        <stop offset="95%" stopColor="#f59e0b" stopOpacity={0.1}/>
                                    </linearGradient>
                                    <linearGradient id="colorLoad" x1="0" y1="0" x2="0" y2="1">
                                        <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.3}/>
                                        <stop offset="95%" stopColor="#3b82f6" stopOpacity={0.05}/>
                                    </linearGradient>
                                </defs>
                                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                                <XAxis 
                                    dataKey={getXAxisKey()} 
                                    stroke="#6b7280"
                                    tick={{ fill: '#6b7280' }}
                                />
                                <YAxis 
                                    stroke="#6b7280"
                                    tick={{ fill: '#6b7280' }}
                                    label={{ value: 'MW', angle: -90, position: 'insideLeft', style: { textAnchor: 'middle', fill: '#6b7280' } }}
                                />
                                <Tooltip 
                                    contentStyle={{ 
                                        backgroundColor: '#fff', 
                                        border: '1px solid #e5e7eb',
                                        borderRadius: '8px'
                                    }}
                                    formatter={(value) => {
                                        if (typeof value === 'number') {
                                            return `${value.toFixed(2)} MW`;
                                        }
                                        return value;
                                    }}
                                />
                                {selectedMode === 'all' && (
                                    <Legend 
                                        wrapperStyle={{ paddingTop: '20px' }}
                                        iconType="square"
                                        formatter={(value) => {
                                            const labels = {
                                                transitLoad: 'Transit',
                                                commercialLoad: 'Commercial',
                                                passengerLoad: 'Passenger'
                                            };
                                            return labels[value] || value;
                                        }}
                                    />
                                )}
                                {selectedMode === 'all' ? (
                                    <>
                                        <Area 
                                            type="monotone" 
                                            dataKey="transitLoad" 
                                            name="Transit"
                                            stackId="1"
                                            stroke="#f59e0b" 
                                            strokeWidth={2}
                                            fill="url(#colorTransit)" 
                                        />
                                        <Area 
                                            type="monotone" 
                                            dataKey="commercialLoad" 
                                            name="Commercial"
                                            stackId="1"
                                            stroke="#10b981" 
                                            strokeWidth={2}
                                            fill="url(#colorCommercial)" 
                                        />
                                        <Area 
                                            type="monotone" 
                                            dataKey="passengerLoad" 
                                            name="Passenger"
                                            stackId="1"
                                            stroke="#3b82f6" 
                                            strokeWidth={2}
                                            fill="url(#colorPassenger)" 
                                        />
                                    </>
                                ) : (
                                    <Area 
                                        type="monotone" 
                                        dataKey="load" 
                                        stroke="#3b82f6" 
                                        strokeWidth={2}
                                        fill="url(#colorLoad)" 
                                    />
                                )}
                            </AreaChart>
                        </ResponsiveContainer>
                    </div>

                    {/* Substation Data Section */}
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
                        <div className="bg-gray-200 rounded-lg p-6 mb-8">
                            <h2 className="text-xl font-bold text-gray-800 mb-4">Substation data</h2>
                            <ul className="space-y-2 text-gray-800">
                                <li className="font-semibold">• Substation statistics </li>
                                <ul className="ml-6 space-y-1">
                                    <li>• Total substations in region: 12</li>
                                    <li>• Average capacity utilization: 68%</li>
                                    <li>• Peak load transformer capacity: 250 MVA</li>
                                </ul>
                                <li className="font-semibold mt-3">• Substation statistics</li>
                                <ul className="ml-6 space-y-1">
                                    <li>• Substations requiring upgrades: 3</li>
                                    <li>• Estimated upgrade cost: $2.4M</li>
                                    <li>• Transformer reserve capacity: 85 MVA</li>
                                </ul>
                            </ul>
                        </div>

                        {/* Circuit Profile Data Section */}
                        <div className="bg-gray-200 rounded-lg p-6 mb-8">
                            <h2 className="text-xl font-bold text-gray-800 mb-4">Circuit profile data</h2>
                            <ul className="space-y-2 text-gray-800">
                                <li className="font-semibold">• Circuit profile statistic</li>
                                <ul className="ml-6 space-y-1">
                                    <li>• Total distribution circuits: 47</li>
                                    <li>• Average circuit loading: 72%</li>
                                    <li>• Circuits at &gt;85% capacity: 8</li>
                                </ul>
                                <li className="font-semibold mt-3">• Circuit profile statistic</li>
                                <ul className="ml-6 space-y-1">
                                    <li>• Voltage regulation compliance: 98%</li>
                                    <li>• Load balancing efficiency: 94%</li>
                                    <li>• Emergency transfer capability: Available</li>
                                    <li>• Circuit reconfiguration flexibility: High</li>
                                </ul>
                            </ul>
                        </div>
                    </div>
            </div>
        </div>
    );
}

export default UtilityScenarioStudy;

